type AttributeValueLike = {
  id?: number;
  effect_price?: number | null;
};

/** One attribute value as the POS/product APIs return it, with its parent attribute inlined. */
type AttributeValueForGrouping = AttributeValueLike & {
  status?: number | null;
  attribute?: {
    id?: number;
    name?: any;
    type?: any;
    status?: any;
  } | null;
};

/** Attribute values regrouped under their attribute, for rendering one input per attribute. */
export type GroupedAttribute<T> = {
  id: number;
  name: any;
  type: any;
  status: any;
  values: T[];
};

type ProductWithConfiguration = {
  unit?: Array<{ id?: number; units_type_id?: number }>;
  units?: Array<{ id?: number; units_type_id?: number }>;
  units_count?: number;
  attributes?: AttributeValueLike[];
  extras?: unknown[];
  min_quantity?: number;
  max_quantity?: number;
  step?: number;
  steps?: number;
  is_offer?: number | null;
  offer?: { id?: number } | null;
};

/** Where the offer stands for this line: an offer dropped by the agent must not hide anything. */
export type OfferContext = {
  /** Product is being added again without its offer ("add product without offer"). */
  forceAddMode?: boolean;
  /** Cart line being edited — a line with no offer_id lost its offer. */
  cartItem?: { offer_id?: number | null } | null;
};

const CART_QUANTITY_ERROR_CODES = new Set([
  "quantity_below_min",
  "quantity_above_max",
]);

/** Structured cart API errors (e.g. quantity bounds) — not Laravel field-validation maps. */
export function isStructuredCartQuantityError(errors: unknown): boolean {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    return false;
  }
  const code = (errors as Record<string, unknown>).code;
  return typeof code === "string" && CART_QUANTITY_ERROR_CODES.has(code);
}

/** Normalize min/max quantity fields from POS filter or product detail payloads. */
export function normalizeProductQuantityBounds<T extends ProductWithConfiguration>(
  product: T
): T {
  if (!product || typeof product !== "object") {
    return product;
  }

  const raw = product as T & Record<string, unknown>;
  const min =
    product.min_quantity ??
    (typeof raw.minQuantity === "number" ? raw.minQuantity : undefined) ??
    (typeof raw.minimum_quantity === "number" ? raw.minimum_quantity : undefined);
  const max =
    product.max_quantity ??
    (typeof raw.maxQuantity === "number" ? raw.maxQuantity : undefined) ??
    (typeof raw.maximum_quantity === "number" ? raw.maximum_quantity : undefined);

  if (min === undefined && max === undefined) {
    return product;
  }

  return {
    ...product,
    ...(min !== undefined && { min_quantity: min }),
    ...(max !== undefined && { max_quantity: max }),
  };
}

export function getProductUnits(product: ProductWithConfiguration) {
  return product.unit || product.units || [];
}

export function getMinQuantity(product: ProductWithConfiguration): number {
  const min = product.min_quantity ?? product.step ?? product.steps ?? 1;
  return min > 0 ? min : 1;
}

export function getQuantityStep(product: ProductWithConfiguration): number {
  const step = product.steps ?? product.step ?? 1;
  return step > 0 ? step : 1;
}

export function getMaxQuantity(product: ProductWithConfiguration): number | null {
  if (product.max_quantity == null) return null;
  return product.max_quantity > 0 ? product.max_quantity : null;
}

export function productRequiresQuantityInput(product: ProductWithConfiguration): boolean {
  return getMinQuantity(product) > 1;
}

/** An offer is priced on the product itself, so it is the offer that decides what stays selectable. */
export function productOfferIsActive(
  product: ProductWithConfiguration,
  context: OfferContext = {}
): boolean {
  if (!product) return false;
  if (context.forceAddMode) return false;
  if (context.cartItem && !context.cartItem.offer_id) return false;
  return Boolean(product.offer) || product.is_offer === 1;
}

/** effect_price: 0 (or null) = no price impact, 1 = increase, 2 = discount. */
export function attributeValueAffectsPrice(value: AttributeValueLike): boolean {
  const effect = value?.effect_price;
  if (effect == null) return false;
  return Number(effect) !== 0;
}

/**
 * Attribute values selectable for this line. Under an offer, price-affecting values are
 * dropped — they would override the offer price. Values already stored on the cart line are
 * kept so editing an existing line never silently strips what was picked before.
 */
export function getSelectableAttributeValues<T extends AttributeValueLike>(
  values: T[],
  offerActive: boolean,
  keepValueIds: Iterable<number> = []
): T[] {
  if (!Array.isArray(values)) return [];
  if (!offerActive) return values;

  const keep = new Set(keepValueIds);
  return values.filter(
    (value) => !attributeValueAffectsPrice(value) || (value?.id != null && keep.has(value.id))
  );
}

/** Attribute value IDs already stored on a cart line, so editing never drops what was picked. */
export function getCartItemAttributeValueIds(cartItem: any): number[] {
  return (cartItem?.attributes || [])
    .map((attr: any) => Number(attr?.attribute_value_id))
    .filter((id: number) => Number.isFinite(id));
}

/**
 * Group attribute values by their attribute for display.
 * Disabled attributes/values are dropped, and under an offer so are price-affecting values.
 */
export function groupAttributeValues<T extends AttributeValueForGrouping>(
  attributeValues: T[],
  offerActive: boolean,
  keepValueIds: Iterable<number> = []
): GroupedAttribute<T>[] {
  const activeAttributeValues = (attributeValues || []).filter((attrValue) => {
    if (attrValue?.status === undefined || attrValue?.status === null) return true;
    return attrValue.status !== 0 && attrValue.attribute?.status !== 0;
  });

  const selectable = getSelectableAttributeValues(activeAttributeValues, offerActive, keepValueIds);

  const grouped: { [key: number]: GroupedAttribute<T> } = {};
  selectable.forEach((attrValue) => {
    const attributeId = attrValue.attribute?.id;
    if (attributeId == null) return;
    if (!grouped[attributeId]) {
      grouped[attributeId] = {
        id: attributeId,
        name: attrValue.attribute?.name,
        type: attrValue.attribute?.type,
        status: attrValue.attribute?.status,
        values: [],
      };
    }
    grouped[attributeId].values.push(attrValue);
  });

  return Object.values(grouped);
}

export function productRequiresConfiguration(
  product: ProductWithConfiguration,
  context: OfferContext = {}
): boolean {
  const units = getProductUnits(product);

  const hasMultiUnits =
    (product.units_count != null && product.units_count > 1) ||
    units.length > 1;

  const hasAttributes =
    Array.isArray(product.attributes) &&
    getSelectableAttributeValues(
      product.attributes,
      productOfferIsActive(product, context)
    ).length > 0;

  const hasExtras =
    Array.isArray(product.extras) && product.extras.length > 0;

  return (
    hasMultiUnits ||
    hasAttributes ||
    hasExtras ||
    productRequiresQuantityInput(product)
  );
}

export function getDefaultUnitId(product: ProductWithConfiguration): number | null {
  const units = getProductUnits(product);
  if (units.length !== 1) return null;
  const unit = units[0];
  return unit?.id ?? unit?.units_type_id ?? null;
}

export function getEffectiveMaxQuantity(
  product: ProductWithConfiguration,
  availableStock: number
): number {
  const configuredMax = getMaxQuantity(product);
  if (configuredMax == null) return availableStock;
  return Math.min(configuredMax, availableStock);
}

export function readQuantityBelowMinimumError(error: unknown): {
  min: number;
  max: number | null;
  message: string;
} | null {
  const apiData = (error as { response?: { data?: { message?: string; errors?: Record<string, unknown> } } })
    ?.response?.data;
  const apiErrors = apiData?.errors;
  if (apiErrors?.code !== "quantity_below_min") return null;

  const min = typeof apiErrors.min === "number" ? apiErrors.min : 1;
  const max = typeof apiErrors.max === "number" ? apiErrors.max : null;
  const message =
    typeof apiErrors.message === "string"
      ? apiErrors.message
      : typeof apiData?.message === "string"
        ? apiData.message
        : "";

  return { min, max, message };
}
